/**
 * @file Bulk actions for the staged mapping-review row grid. The scope
 * `Segmented` (this section / all sections) lives in the visible bar — not
 * buried in the menu — so it never silently changes what a select targets and
 * keeps the menu's roving-tabindex intact; it is disabled, never hidden, for a
 * single-section table or the combined view (where scope is forced to "all").
 * Tag and low-confidence selects REPLACE the selection (showing a live in-scope
 * count) and an Invert action covers the complement, instead of a silent union.
 * Confirms gate on reversibility, not row count: the irreversible Clear and
 * Restore always confirm; a reversible Skip only warns past
 * `BULK_CONFIRM_THRESHOLD`. A non-binding counter surfaces untouched
 * low-confidence rows that still need review. Every control freezes while
 * `useMappingReviewSubmitting()` is true, and all user copy renders through
 * `mappingReviewLabels`.
 */
import { FC, useState } from 'react';
import { Button, Dropdown, Modal, Segmented, Tag, Tooltip, Typography } from 'antd';
import { CloseOutlined, DownOutlined, StopOutlined, UndoOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useSnapshot } from 'valtio';
import { BULK_CONFIRM_THRESHOLD, CONFIDENCE_WARN_THRESHOLD } from '#types';
import FlexBox from '../../atoms/FlexBox';
import FlexCol from '../../atoms/FlexCol';
import {
  clearSelection,
  rejectRows,
  restoreRows,
  selectRowsWhere,
} from './mappingReviewSession';
import type { MappingReviewTableSlice } from './mappingReviewState';
import { mappingReviewLabels } from './mappingReviewLabels';
import { useMappingReviewSubmitting } from './useMappingReviewSubmitting';

const { Text } = Typography;

interface MappingReviewBulkBarProps {
  slice: MappingReviewTableSlice;
}

interface BulkConfirm {
  title: string;
  body: string;
  okText: string;
  /** Irreversible actions (Clear / Restore) flag the OK button as destructive. */
  danger: boolean;
  run: () => void;
}

const MappingReviewBulkBar: FC<MappingReviewBulkBarProps> = ({ slice }) => {
  const snap = useSnapshot(slice);
  const submitting = useMappingReviewSubmitting();
  const [scope, setScope] = useState<'chunk' | 'all'>('all');
  const [confirm, setConfirm] = useState<BulkConfirm | null>(null);

  const groupId = slice.groupId;
  const chunks = snap.chunks;
  const singleChunk = chunks.length <= 1;
  const combinedView = !singleChunk && snap.activeChunkId === null;
  const effectiveScope = singleChunk || combinedView ? 'all' : scope;
  const selectedCount = snap.selection.count;
  const sectionLabel = mappingReviewLabels.section;
  const warnPct = Math.round(CONFIDENCE_WARN_THRESHOLD * 100);

  const scopePredicate = (): ((index: number) => boolean) => {
    if (effectiveScope === 'all') return () => true;
    const activeIndex = chunks.findIndex((c) => c.label.id === snap.activeChunkId);
    if (activeIndex < 0) return () => false;
    const start = chunks.slice(0, activeIndex).reduce((n, c) => n + c.rows.length, 0);
    const end = start + chunks[activeIndex].rows.length;
    return (index) => index >= start && index < end;
  };

  const classifications = snap.baseRows;
  const inScope = scopePredicate();
  const noiseCount = classifications.filter((c, i) => c.tag === 'junk' && inScope(i)).length;
  const summaryCount = classifications.filter((c, i) => c.tag === 'summary' && inScope(i)).length;
  const lowConfidenceCount = classifications.filter(
    (c, i) => (c.confidence ?? 1) < CONFIDENCE_WARN_THRESHOLD && inScope(i),
  ).length;
  const needsEyes = classifications.filter(
    (c, i) =>
      (c.confidence ?? 1) < CONFIDENCE_WARN_THRESHOLD && !snap.rejectedRows[i] && !snap.cellOverrides[i],
  ).length;

  const selectedIndexes = (): number[] => Object.keys(slice.selection.selected).map(Number);

  /** Reversible skip: only warns once the count crosses the confirm threshold. */
  const skipConfirm = (title: string, indexes: number[], run: () => void) => {
    if (indexes.length < BULK_CONFIRM_THRESHOLD) {
      run();
      return;
    }
    setConfirm({
      title,
      body: `These ${indexes.length} rows will be skipped at write. They stay visible and can be restored.`,
      okText: `${mappingReviewLabels.skip} ${indexes.length} rows`,
      danger: false,
      run,
    });
  };

  const replaceSelection = (predicate: (index: number) => boolean) => {
    clearSelection(groupId);
    selectRowsWhere(groupId, predicate);
  };

  const invertSelection = () => {
    const wasSelected = { ...slice.selection.selected };
    clearSelection(groupId);
    selectRowsWhere(groupId, (i) => inScope(i) && !wasSelected[i]);
  };

  const skipAllNoise = () => {
    const noise = classifications
      .map((c, index) => ({ tag: c.tag, index }))
      .filter(({ tag, index }) => tag === 'junk' && inScope(index))
      .map(({ index }) => index);
    skipConfirm(`${mappingReviewLabels.skip} all ${mappingReviewLabels.rowClass.junk} rows`, noise, () =>
      rejectRows(groupId, noise),
    );
  };

  const items: MenuProps['items'] = [
    {
      key: 'select-noise',
      label: `Select all ${mappingReviewLabels.rowClass.junk} rows (${noiseCount})`,
      disabled: submitting || noiseCount === 0,
      onClick: () => replaceSelection((i) => inScope(i) && classifications[i]?.tag === 'junk'),
    },
    {
      key: 'select-summary',
      label: `Select all ${mappingReviewLabels.rowClass.summary} rows (${summaryCount})`,
      disabled: submitting || summaryCount === 0,
      onClick: () => replaceSelection((i) => inScope(i) && classifications[i]?.tag === 'summary'),
    },
    {
      key: 'select-low-confidence',
      label: `Select low-confidence rows (< ${warnPct}%) (${lowConfidenceCount})`,
      disabled: submitting || lowConfidenceCount === 0,
      onClick: () =>
        replaceSelection(
          (i) => inScope(i) && (classifications[i]?.confidence ?? 1) < CONFIDENCE_WARN_THRESHOLD,
        ),
    },
    {
      key: 'invert',
      label: 'Invert selection',
      disabled: submitting,
      onClick: invertSelection,
    },
    { type: 'divider' },
    {
      key: 'skip-selected',
      icon: <StopOutlined />,
      label: `${mappingReviewLabels.skip} selected`,
      disabled: submitting || selectedCount === 0,
      onClick: () => {
        const indexes = selectedIndexes();
        skipConfirm(`${mappingReviewLabels.skip} selected rows`, indexes, () => {
          rejectRows(groupId, indexes);
          clearSelection(groupId);
        });
      },
    },
    {
      key: 'restore-selected',
      icon: <UndoOutlined />,
      label: `${mappingReviewLabels.restore} selected`,
      disabled: submitting || selectedCount === 0,
      onClick: () => {
        const indexes = selectedIndexes();
        setConfirm({
          title: `${mappingReviewLabels.restore} selected rows`,
          body: `This restores ${indexes.length} rows you skipped. Their skip decisions are cleared.`,
          okText: `${mappingReviewLabels.restore} ${indexes.length} rows`,
          danger: true,
          run: () => {
            restoreRows(groupId, indexes);
            clearSelection(groupId);
          },
        });
      },
    },
    {
      key: 'skip-noise',
      icon: <StopOutlined />,
      label: `${mappingReviewLabels.skip} all ${mappingReviewLabels.rowClass.junk} rows`,
      disabled: submitting || noiseCount === 0,
      onClick: skipAllNoise,
    },
    { type: 'divider' },
    {
      key: 'clear',
      icon: <CloseOutlined />,
      label: 'Clear selection',
      disabled: submitting || selectedCount === 0,
      onClick: () =>
        setConfirm({
          title: 'Clear selection',
          body: `This clears your selection of ${selectedCount} rows.`,
          okText: 'Clear selection',
          danger: true,
          run: () => clearSelection(groupId),
        }),
    },
  ];

  return (
    <>
      <FlexBox noGrow gap={10} justifyStart>
        <Dropdown menu={{ items }} trigger={['click']} disabled={submitting}>
          <Button size="small" type="link" disabled={submitting} aria-label="Mapping review bulk actions">
            <FlexBox noGrow gap={5}>
              <Text>Actions</Text>
              <DownOutlined />
              <Tag style={{ margin: 0, borderRadius: 20 }}>{selectedCount}</Tag>
            </FlexBox>
          </Button>
        </Dropdown>
        <Tooltip title={singleChunk ? `This table has a single ${sectionLabel}` : undefined}>
          <Segmented
            size="small"
            aria-label="Bulk action scope"
            disabled={singleChunk || submitting}
            value={effectiveScope}
            onChange={(value) => setScope(value as 'chunk' | 'all')}
            options={[
              {
                value: 'chunk',
                disabled: combinedView,
                label: combinedView ? (
                  <Tooltip title={`Viewing all ${sectionLabel}s — pick a ${sectionLabel} first`}>
                    {`This ${sectionLabel}`}
                  </Tooltip>
                ) : (
                  `This ${sectionLabel}`
                ),
              },
              { value: 'all', label: `All ${sectionLabel}s` },
            ]}
          />
        </Tooltip>
        {needsEyes > 0 && (
          <Text type="secondary" aria-label="Rows needing review">
            {`${needsEyes} ${needsEyes === 1 ? 'row still needs' : 'rows still need'} your eyes`}
          </Text>
        )}
      </FlexBox>
      <Modal
        open={confirm !== null}
        title={confirm?.title}
        okText={confirm?.okText}
        okButtonProps={{ danger: confirm?.danger }}
        onOk={() => {
          confirm?.run();
          setConfirm(null);
        }}
        onCancel={() => setConfirm(null)}
        aria-label="Confirm bulk row action"
      >
        <FlexCol noGap>
          <Text>{confirm?.body}</Text>
        </FlexCol>
      </Modal>
    </>
  );
};

export default MappingReviewBulkBar;
